import React, {useEffect, useState} from 'react';
import {Dialog} from 'primereact/dialog';
import {MapContainer, Marker, TileLayer, useMapEvents} from 'react-leaflet';
import L from 'leaflet';
import {AdminButton} from '../../../layout/admin';
import geocodingService from '../services/geocodingService';
import '../../../common/components/details/DetailsMapSection.scss';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const DEFAULT_COORDINATES: [number, number] = [50.0619474, 19.9368564]; // Cracow centre

interface AddressLocatorProps {
  visible: boolean;
  onHide: () => void;
  address: string;
  initialLatitude?: number;
  initialLongitude?: number;
  onAccept: (latitude: number, longitude: number) => void;
}

const DraggableMarker: React.FC<{
  position: [number, number];
  onPositionChange: (lat: number, lng: number) => void;
}> = ({position, onPositionChange}) => {
  const markerRef = React.useRef<L.Marker>(null);

  const eventHandlers = React.useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const pos = marker.getLatLng();
          onPositionChange(pos.lat, pos.lng);
        }
      },
    }),
    [onPositionChange],
  );

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    />
  );
};

const MapClickHandler: React.FC<{
  onMapClick: (lat: number, lng: number) => void;
}> = ({onMapClick}) => {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const AddressLocator: React.FC<AddressLocatorProps> = ({
  visible,
  onHide,
  address,
  initialLatitude,
  initialLongitude,
  onAccept
}) => {
  const [position, setPosition] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      if (initialLatitude != null && initialLongitude != null) {
        setPosition([initialLatitude, initialLongitude]);
        setError(null);
      } else if (address) {
        geocodeAddress();
      } else {
        setError('No address provided for geocoding');
      }
    }
  }, [visible, address, initialLatitude, initialLongitude]);

  const geocodeAddress = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    const result = await geocodingService.geocodeAddress(address);

    if (result) {
      setPosition([result.latitude, result.longitude]);
    } else {
      setError('Address not found. Please adjust the marker manually or provide a more specific address.');
      setPosition(DEFAULT_COORDINATES);
    }

    setLoading(false);
  };

  const handlePositionChange = (lat: number, lng: number): void => {
    setPosition([lat, lng]);
  };

  const handleAccept = (): void => {
    if (position) {
      onAccept(position[0], position[1]);
      onHide();
    }
  };

  const handleDialogHide = (): void => {
    setPosition(null);
    setError(null);
    onHide();
  };

  return (
    <Dialog
      header="Select Location"
      visible={visible}
      style={{width: '60vw', maxWidth: '900px'}}
      onHide={handleDialogHide}
      maskClassName="bg-black bg-opacity-50"
    >
      <div className="mb-4">
        <p className="text-sm text-gray-700 mb-2">
          <strong>Address:</strong> {address || 'No address provided'}
        </p>
        {error && (
          <p className="text-sm text-amber-700 mb-2">
            {error}
          </p>
        )}
        {loading && (
          <p className="text-sm text-gray-600 mb-2">
            Geocoding address...
          </p>
        )}
        {position && (
          <p className="text-sm text-gray-600">
            <strong>Coordinates:</strong> {position[0].toFixed(6)}, {position[1].toFixed(6)}
          </p>
        )}
      </div>

      {position && (
        <div className="map-container mb-4" style={{height: '400px'}}>
          <MapContainer
            center={position}
            zoom={15}
            scrollWheelZoom={true}
            className="details-map"
            style={{height: '100%'}}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <DraggableMarker
              position={position}
              onPositionChange={handlePositionChange}
            />
            <MapClickHandler onMapClick={handlePositionChange} />
          </MapContainer>
        </div>
      )}

      <div className="text-sm text-gray-600 mb-4">
        <strong>Tip:</strong> Drag the marker or click on the map to adjust the location.
      </div>

      <div className="flex justify-end gap-4 pt-4">
        <AdminButton
          type="button"
          label="Cancel"
          onClick={handleDialogHide}
          variant="white"
        />
        <AdminButton
          type="button"
          label="Accept"
          variant="black"
          onClick={handleAccept}
          disabled={!position}
        />
      </div>
    </Dialog>
  );
};

export default AddressLocator;

